/**
 * This file pre-loads commonly used MUI components to prevent chunk loading errors
 * when they are used in lazy-loaded components.
 */

// Import problematic MUI components
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Collapse from '@mui/material/Collapse';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';

// Import problematic MUI icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Create a registry to track component usage
const componentRegistry = {
  components: new Set(),
  add(component) {
    this.components.add(component);
    return component;
  },
  has(component) {
    return this.components.has(component);
  }
};

// Export them for potential direct use
export { 
  Alert, 
  Snackbar, 
  Collapse, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  Fade,
  Skeleton,
  Paper,
  ExpandMoreIcon,
  CloseIcon,
  ErrorIcon,
  WarningIcon,
  InfoIcon,
  CheckCircleIcon
};

// Export registry for component tracking
export const registry = componentRegistry;

// Export a function that can be called to ensure the module is loaded
export const preloadMuiComponents = () => {
  console.log('MUI components preloaded');
  
  // Register all components
  [
    Alert, 
    Snackbar, 
    Collapse, 
    Accordion, 
    AccordionSummary, 
    AccordionDetails,
    Backdrop,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Slide,
    Fade,
    Skeleton,
    Paper
  ].forEach(component => componentRegistry.add(component));
  
  // Return a reference to all preloaded components
  return { 
    Alert, 
    Snackbar, 
    Collapse, 
    Accordion, 
    AccordionSummary, 
    AccordionDetails,
    Backdrop,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Slide,
    Fade,
    Skeleton,
    Paper,
    ExpandMoreIcon,
    CloseIcon,
    ErrorIcon,
    WarningIcon,
    InfoIcon,
    CheckCircleIcon,
    registry: componentRegistry
  };
};

export default preloadMuiComponents;
